import { useState } from "react";
import { cn } from "../../lib/utils";

interface PublishButtonProps {
  runId: string;
  approvedCount: number;
  onPublish: () => void;
  isPublishing: boolean;
  disabled?: boolean;
}

export function PublishButton({
  runId,
  approvedCount,
  onPublish,
  isPublishing,
  disabled: disabledByCaller = false,
}: PublishButtonProps) {
  const [confirming, setConfirming] = useState(false);

  const disabled = approvedCount === 0 || isPublishing || disabledByCaller;

  if (confirming) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">
          Are you sure? Publish {approvedCount} piece{approvedCount !== 1 ? "s" : ""}?
        </span>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
            "bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800",
          )}
          onClick={() => {
            setConfirming(false);
            onPublish();
          }}
        >
          Confirm
        </button>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
            "bg-slate-100 text-slate-900 hover:bg-slate-200 active:bg-slate-300",
          )}
          onClick={() => setConfirming(false)}
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-medium transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        disabled
          ? "bg-slate-300 text-slate-500"
          : "bg-green-600 text-white hover:bg-green-700 active:bg-green-800",
      )}
      onClick={() => setConfirming(true)}
    >
      {isPublishing && (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      Publish {approvedCount} Piece{approvedCount !== 1 ? "s" : ""}
      {approvedCount > 0 && (
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-semibold">
          {approvedCount}
        </span>
      )}
    </button>
  );
}
